import { useRenderingSettings } from "@/components/common/rendering-settings-context";
import { useSceneEvents } from "@/components/common/scene-events-context";
import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import { usePreload3DObjects, type PointCloudObject } from "@/object-cache";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import { useCurrentScene } from "@/modes/mode-data-context";
import { useCameraRotationFor2dOverviewAndMiniMap } from "@/hooks/use-center-camera-on-placeholders";
import { isIElementOverviewImage } from "@faro-lotv/ielement-types";
import { getEnvAndRegionFromOrigin } from "@/utils/env-utils";
import { convertToDateString, isLocalhost } from "@faro-lotv/foundation";
import {
  selectIElementWorldMatrix,
  selectProjectName,
} from "@faro-lotv/project-source";
import { useThree } from "@react-three/fiber";
import { useEffect, useMemo } from "react";
import { Matrix4 } from "three";
import {
  loadSurfaceReportMaps,
  SurfaceReportMapCapture,
  SURFACE_REPORT_MAP_TIMEOUT,
} from "./surface-report-map";
import type { StoreApi } from "zustand/vanilla";
import {
  surfaceReportCamera,
  surfaceReportLink,
} from "./surface-report-camera";
import {
  createSurfaceReportPdf,
  snapshotSurfaceSelection,
  surfaceReportEntry,
  SURFACE_REPORT_LOGO,
  type SurfaceReportData,
} from "./surface-report";
import type { SurfaceWandState } from "./surface-wand-context";
import { isAnalyticSelection } from "./wand-selection";

const LOGO_TIMEOUT = 5000;

/**
 * @param source Current cloud; capture creates a separate view and never changes its material.
 * @param store Canvas-local selections.
 * @param active Whether this canvas currently owns the wand toolbar.
 */
export function useSurfaceReport(
  source: PointCloudObject,
  store: StoreApi<SurfaceWandState>,
  active: boolean,
): void {
  const camera = useThree((state) => state.camera);
  const events = useSceneEvents();
  const unit = useDistanceUnit();
  const projectName = useAppSelector(selectProjectName);
  const appStore = useAppStore();
  const { activeSheets, availableSheets } = useCurrentScene();
  const mapRotation = useCameraRotationFor2dOverviewAndMiniMap();
  const mapElements = useMemo(() => {
    if (!active) return [];
    return activeSheets.length
      ? activeSheets
      : availableSheets.filter(isIElementOverviewImage);
  }, [active, activeSheets, availableSheets]);
  const mapPromises = usePreload3DObjects(mapElements, "SurfaceReportMaps");
  const { gapFilling, eyeDomeOn } = useRenderingSettings();
  useEffect(() => {
    if (!active) return;
    let job: AbortController | undefined;
    async function generate(controller: AbortController): Promise<void> {
      const { signal } = controller;
      const state = store.getState();
      const candidates = [
        ...state.retainedSelections,
        ...(state.selection ? [state.selection] : []),
      ];
      if (!candidates.every(isAnalyticSelection)) {
        throw new Error(
          "Pipe run reports are not yet available. Remove pipe run selections before creating an analytic surface report.",
        );
      }
      const selections = candidates.map(snapshotSurfaceSelection);
      if (!selections.length || state.request) {
        throw new Error("Finish selecting a surface before creating a report.");
      }
      if (
        selections.some((selection) => selection.cloudId !== source.iElement.id)
      ) {
        throw new Error(
          "A selection no longer belongs to the active point cloud.",
        );
      }
      const viewer = {
        position: camera.position.clone(),
        up: camera.up.clone(),
      };
      source.updateWorldMatrix(true, false);
      const bounds = source.boundingBox
        .clone()
        .applyMatrix4(source.matrixWorld);
      const report: SurfaceReportData = {
        projectName: projectName ?? "",
        date: convertToDateString(new Date().toISOString()),
        entries: [],
      };
      events.surfaceWandReportState.emit({
        phase: "loading",
        completed: 0,
        total: selections.length,
      });
      const rotation = mapRotation.clone();
      const mapTransforms = mapElements.map((sheet) =>
        new Matrix4().fromArray(
          selectIElementWorldMatrix(sheet.id)(appStore.getState()),
        ),
      );
      const maps = await loadSurfaceReportMaps(
        mapPromises,
        AbortSignal.any([
          signal,
          AbortSignal.timeout(SURFACE_REPORT_MAP_TIMEOUT),
        ]),
      );
      const { SurfaceReportCapture } = await import("./surface-report-capture");
      signal.throwIfAborted();
      const capture = new SurfaceReportCapture(source, {
        gapFilling,
        eyeDomeOn,
      });
      let mapCapture: SurfaceReportMapCapture | undefined;
      try {
        if (maps.length) {
          mapCapture = new SurfaceReportMapCapture(
            maps.map((sheet, index) => ({
              sheet,
              transform: mapTransforms[index],
            })),
          );
        }
        for (const selection of selections) {
          const { camera: reportCamera, target } = surfaceReportCamera(
            selection,
            viewer,
            bounds,
          );
          const images = await capture.capture(selection, reportCamera, signal);
          const cylinderAnalysis = selection.cylinder
            ? await capture.captureCylinderAnalysis(selection, unit, signal)
            : undefined;
          const label = `${selection.cylinder ? "Cylinder" : "Plane"} ${report.entries.length + 1}`;
          const overviewMap = mapCapture
            ? await mapCapture.capture(selection, rotation, label, signal)
            : await capture.captureMapFallback(
                selection,
                rotation,
                label,
                signal,
              );
          signal.throwIfAborted();
          report.entries.push(
            surfaceReportEntry(
              selection,
              report.entries.length,
              viewer.up,
              unit,
              {
                ...images,
                cylinderAnalysis,
                overviewMap,
                mapCaption: mapCapture
                  ? "Location on overview map"
                  : "Top-down point-cloud location (no overview map)",
              },
              surfaceReportLink(selection.cloudId, reportCamera, target),
            ),
          );
          events.surfaceWandReportState.emit({
            phase: "loading",
            completed: report.entries.length,
            total: selections.length,
          });
        }
      } finally {
        mapCapture?.dispose();
        capture.dispose();
      }
      const logo = await fetch(SURFACE_REPORT_LOGO, {
        signal: AbortSignal.any([signal, AbortSignal.timeout(LOGO_TIMEOUT)]),
      })
        .then(async (response) =>
          response.ok
            ? new Uint8Array(await response.arrayBuffer())
            : undefined,
        )
        .catch(() => undefined);
      signal.throwIfAborted();
      const region = isLocalhost(window.location.hostname)
        ? "com"
        : getEnvAndRegionFromOrigin(window.location.origin)[1];
      const pdf = createSurfaceReportPdf(
        report,
        region === "eu" ? "a4" : "letter",
        logo,
      ).output("blob");
      signal.throwIfAborted();
      events.surfaceWandReportState.emit({ phase: "ready", report, pdf });
    }
    const listener = events.surfaceWandReport.on((action) => {
      if (action === "cancel") {
        job?.abort();
        job = undefined;
        events.surfaceWandReportState.emit({ phase: "idle" });
        return;
      }
      if (job) return;
      const controller = new AbortController();
      job = controller;
      void generate(controller)
        .catch((error: unknown) => {
          if (!controller.signal.aborted) {
            events.surfaceWandReportState.emit({
              phase: "error",
              message:
                error instanceof Error
                  ? error.message
                  : "The report could not be created.",
            });
          }
        })
        .finally(() => {
          if (job === controller) job = undefined;
        });
    });
    return () => {
      listener.dispose();
      job?.abort();
      events.surfaceWandReportState.emit({ phase: "idle" });
    };
  }, [
    active,
    camera,
    source,
    store,
    events,
    unit,
    projectName,
    gapFilling,
    eyeDomeOn,
    appStore,
    mapRotation,
    mapElements,
    mapPromises,
  ]);
}
